import { FormEvent, useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useSession } from '@/app/providers/session-provider';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { courseQueries } from '@/entities/course';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/toast-provider';
import { normalizeEmail } from '@/shared/utils/normalize-email';

export const SignInPage = () => {
  const router = useRouter();
  const toast = useToast();
  const { signIn, authError, clearAuthError } = useSession();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setError(null);
    clearAuthError();

    let signedIn = false;
    try {
      setIsLoading(true);
      const nextUser = await signIn({
        email: normalizeEmail(email),
        password,
      });
      signedIn = true;
      const courses = await queryClient.fetchQuery(courseQueries.list());
      const contents = courses[0]?.contents ?? [];
      const courseIdParam = courses[0]?.id;
      const nextUnCompletedContent = contents.find(
        (content) => !content?.user_content_association?.is_completed,
      );

      if (!nextUser?.is_email_verified) {
        router.navigate({
          to: '/verify-email',
          search: {
            email: nextUser?.email,
            token: undefined,
          },
        });
        toast({
          type: 'warning',
          title: 'Email not verified',
          description: 'Please verify your email to continue.',
        });
        return;
      }

      if (!nextUser?.user_can_take) {
        router.navigate({
          to: '/payment/select-course',
          search: {
            status: undefined,
            session_id: undefined,
          },
        });
        return;
      }

      toast({
        type: 'success',
        title: 'Sign-in successful',
        description: 'Welcome back.',
      });

      if (nextUser?.recent_user_content_association) {
        const { course_id, content_id } =
          nextUser.recent_user_content_association;
        router.navigate({
          to: '/courses/plyer',
          search: {
            courseId: course_id,
            contentId: content_id,
          },
        });
        return;
      }

      if (nextUnCompletedContent?.content_type === 'quiz') {
        if (!courseIdParam) {
          router.navigate({ to: '/courses' });
          return;
        }
        const nextQuiz = nextUnCompletedContent.quizzes.find(
          (quiz) =>
            !quiz?.user_content_quiz_association ||
            quiz?.user_content_quiz_association?.answer_bool === null,
        );
        if (!nextQuiz?.id) {
          router.navigate({ to: '/courses' });
          return;
        }
        router.navigate({
          to: '/courses/quiz',
          search: {
            courseId: courseIdParam,
            contentId: nextUnCompletedContent.id,
            quizId: nextQuiz.id,
          },
        });
        return;
      }

      router.navigate({ to: '/courses' });
    } catch {
      if (signedIn) {
        // Auth succeeded, but loading course data / routing failed. Give explicit
        // feedback instead of a silent frozen form, and fall back to the courses list.
        toast({
          type: 'error',
          title: 'Could not load your courses',
          description: 'Please try again in a moment.',
        });
        router.navigate({ to: '/courses' });
      }
      // else: signIn() failure is surfaced inline via authError by SessionProvider.
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-3 py-10 text-slate-50">
      <img
        src="https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?q=80&w=2069&auto=format&fit=crop"
        alt="Modern workspace background"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/65" aria-hidden />

      <Link
        to="/"
        className="group absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/60 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-slate-950/40 backdrop-blur transition hover:bg-slate-900/80 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition group-hover:-translate-x-0.5" />
        Back
      </Link>

      <section className="relative z-10 w-full max-w-[420px] space-y-6 rounded-[28px] border border-white/10 bg-slate-900/75 p-8 text-slate-50 shadow-[0_30px_80px_rgba(10,17,33,0.55)] backdrop-blur-2xl">
        <header className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-white">
            Welcome Back
          </h1>
          <p className="text-sm text-slate-200/80">
            Sign in to continue your education journey
          </p>
        </header>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <label htmlFor="email" className="sr-only">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                placeholder="example@jigo.ai"
                value={email}
                onChange={(event) => {
                  if (error) setError(null);
                  clearAuthError();
                  setEmail(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => {
                  if (error) setError(null);
                  clearAuthError();
                  setPassword(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pr-11 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-200/80 transition hover:bg-white/10 hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-sky-300 transition hover:text-sky-200"
              >
                Forgot Password?
              </Link>
            </div>
          </div>

          {error ? (
            <p className="text-sm font-semibold text-amber-300">{error}</p>
          ) : null}
          {!error && authError ? (
            <p className="text-sm font-semibold text-amber-300">{authError}</p>
          ) : null}

          <Button
            type="submit"
            variant="glow"
            size="lg"
            className="w-full gap-2 text-sm h-11"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
            {!isLoading ? <ArrowRight className="h-4 w-4 transition" /> : null}
          </Button>
        </form>

        <footer className="flex items-center justify-center gap-2 text-sm text-slate-200/80 pt-2">
          <span>Don't have an account?</span>
          <Link
            to="/register"
            className="font-semibold text-sky-300 transition hover:text-sky-200"
          >
            Create Account
          </Link>
        </footer>
      </section>

      {isLoading && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <span className="h-12 w-12 animate-spin rounded-full border-4 border-slate-400/40 border-t-blue-400" />
        </div>
      )}
    </main>
  );
};
